<template>
  <v-container fluid class="pa-2">
    <!-- Header -->
    <v-card outlined class="rounded-lg mb-3 pre-accounting-header">
      <v-card-text class="pa-4">
        <div class="d-flex align-center flex-wrap" style="gap: 16px;">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="40" class="mr-3">
              <v-icon dark>mdi-calculator-variant</v-icon>
            </v-avatar>
            <div>
              <div class="text-overline grey--text text--darken-1" style="line-height: 1;">
                Pre-Contabilidad
              </div>
              <div class="text-h6 font-weight-bold primary--text" style="line-height: 1.2;">
                Vista {{ showGrouped ? "Agrupada" : "Detallada" }}
              </div>
            </div>
          </div>

          <v-btn-toggle
            v-model="showGrouped"
            mandatory
            dense
            color="primary"
            class="ml-2"
          >
            <v-btn :value="true" small class="text-none">
              <v-icon left small>mdi-group</v-icon>
              Agrupado
            </v-btn>
            <v-btn :value="false" small class="text-none">
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              Detallado
            </v-btn>
          </v-btn-toggle>

          <v-spacer></v-spacer>

          <!-- Panel de balance -->
          <div v-if="!show_credits" class="balance-panel flex-grow-1" style="min-width: 280px; max-width: 520px;">
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="text-caption grey--text text--darken-2 font-weight-medium">
                <v-icon x-small>mdi-scale-balance</v-icon>
                Balance
              </span>
              <v-chip
                :color="balanceStatus.color"
                x-small
                label
                dark
                class="font-weight-bold"
              >
                <v-icon x-small left>{{ balanceStatus.icon }}</v-icon>
                {{ balanceStatus.label }}
              </v-chip>
            </div>
            <v-progress-linear
              :value="balanceStatus.progress"
              :color="balanceStatus.color"
              height="6"
              rounded
              class="mb-2"
            ></v-progress-linear>
            <div class="d-flex justify-space-between text-caption">
              <div>
                <span class="grey--text">Total orden:</span>
                <span class="font-weight-bold ml-1">{{ formatCurrency(totalOrderAmount) }}</span>
              </div>
              <div>
                <span class="grey--text">Total actual:</span>
                <span
                  class="font-weight-bold ml-1"
                  :class="balanceStatus.isBalanced ? 'success--text' : 'error--text'"
                >
                  {{ formatCurrency(Math.abs(currentTotalAmount)) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Resumen débito/crédito -->
          <div v-else class="d-flex flex-wrap" style="gap: 8px;">
            <v-sheet rounded outlined class="px-3 py-2 d-flex align-center" style="border-color: #c8e6c9;">
              <v-icon color="success" class="mr-2">mdi-arrow-up-bold-circle</v-icon>
              <div>
                <div class="text-caption grey--text" style="line-height: 1;">Débito</div>
                <div class="font-weight-bold success--text">
                  {{ currencyFormat(debitTotal) }}
                </div>
              </div>
            </v-sheet>
            <v-sheet rounded outlined class="px-3 py-2 d-flex align-center" style="border-color: #ffcdd2;">
              <v-icon color="error" class="mr-2">mdi-arrow-down-bold-circle</v-icon>
              <div>
                <div class="text-caption grey--text" style="line-height: 1;">Crédito</div>
                <div class="font-weight-bold error--text">
                  {{ currencyFormat(creditTotal) }}
                </div>
              </div>
            </v-sheet>
          </div>

          <!-- Campo de búsqueda -->
          <v-text-field
            v-if="show_credits"
            v-model="search"
            placeholder="Buscar…"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="max-width: 240px;"
          ></v-text-field>

          <v-btn
            v-if="cant_edit"
            color="primary"
            depressed
            class="text-none font-weight-bold rounded-lg"
            @click="openAddDialog"
          >
            <v-icon left>mdi-plus</v-icon>
            Agregar línea
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
    <!-- Tabla principal -->
    <v-card outlined class="rounded-lg overflow-hidden">
      <!-- Barra superior para tabla grande sin show_credits -->
      <div
        v-if="!show_credits && displayedItems.length > 30"
        class="px-4 py-3 d-flex align-center grey lighten-5"
      >
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          placeholder="Buscar…"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          style="max-width: 300px;"
        ></v-text-field>
      </div>

      <v-data-table
        item-key="_rowKey"
        :height="displayedItems.length<=30?'auto':'53vh'"
        :search="search"
        :disable-pagination="displayedItems.length<=30"
        :hide-default-footer="displayedItems.length<=30"
        fixed-footer fixed-header
        :headers="tableHeaders"
        :items="displayedItems"
        :items-per-page="20"
        :dense="displayedItems.length>30"
        class="pre-accounting-table"
        :footer-props="{
          itemsPerPageText: 'Acciones por página:',
          'items-per-page-text': 'Líneas por página',
          'page-text': '{0}-{1} de {2}',
          'select-all-text': 'Seleccionar todo',
          'pagination-text': 'Registros',
          'items-per-page-options': opts,
        }"
      >
        <template v-slot:[`item.amount`]="{ item }" v-if="!show_credits">
          <span class="font-weight-medium" :style="{ color: getAmountColor(item.entry_type_id) }">
            {{ formatCurrency(item.amount) }}
          </span>
        </template>
        <template v-slot:[`item.debit`]="{ item }" v-if="show_credits">
          <span class="font-weight-medium success--text">
            {{ item.entry_type_id==1?formatCurrency(item.amount):'' }}
          </span>
        </template>
        <template v-slot:[`item.credit`]="{ item }" v-if="show_credits">
          <span class="font-weight-medium error--text">
            {{ item.entry_type_id==2?formatCurrency(item.amount):'' }}
          </span>
        </template>
        <template v-slot:[`item.location_name`]="{ item }">
          {{ getLocationName(item.location_id) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-menu bottom left origin="center center" transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-if="cant_edit" v-bind="attrs" v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openEditDialog(item)">
                <v-list-item-icon class="mr-3">
                  <v-icon color="primary" small>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Editar línea</v-list-item-title>
              </v-list-item>
              <v-list-item @click="copyLine(item)">
                <v-list-item-icon class="mr-3">
                  <v-icon color="primary" small>mdi-content-copy</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Copiar línea</v-list-item-title>
              </v-list-item>
              <v-list-item @click="removeAccount(item)">
                <v-list-item-icon class="mr-3">
                  <v-icon color="error" small>mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="error--text">Eliminar línea</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>

      <!-- Acciones inferiores -->
      <v-divider v-if="cant_edit"></v-divider>
      <v-card-actions v-if="cant_edit" class="pa-3 grey lighten-5">
        <v-btn
          v-if="show_cuadre"
          text
          color="primary"
          class="text-none"
          @click="adjustDecimals"
        >
          <v-icon left small>mdi-auto-fix</v-icon>
          Cuadrar decimales
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          depressed
          class="text-none font-weight-bold rounded-lg"
          @click="save_all"
          :disabled="Math.abs(currentTotalAmount).toFixed(2)!=(totalOrderAmount).toFixed(2) || !edited"
        >
          <v-icon left>mdi-content-save</v-icon>
          Guardar Cambios
        </v-btn>
      </v-card-actions>
    </v-card>
    <!-- Dialogo de agregar/editar -->
    <v-dialog v-model="showEditDialog" width="90%" max-width="720" scrollable>
      <v-card class="rounded-xl overflow-hidden">
        <!-- Header del diálogo -->
        <div class="pre-accounting-dialog-header px-5 py-3 d-flex align-center">
          <v-avatar color="white" size="38" class="mr-3 elevation-2">
            <v-icon color="primary">{{ isEditing ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="white--text text-h6 font-weight-bold" style="line-height: 1.1;">
              {{ isEditing ? "Editar línea" : "Agregar línea" }}
            </div>
            <div class="white--text text-caption" style="opacity: 0.85;">
              Completa los datos contables
            </div>
          </div>
          <v-btn icon dark @click="showEditDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-5">
          <!-- Selección de artículo (solo modo detallado al agregar) -->
          <v-row dense v-if="!isEditing && !showGrouped">
            <v-col cols="12" md="8">
              <v-autocomplete
                v-model="editForm.item"
                :items="items_list"
                label="Artículo"
                hint="Obligatorio"
                persistent-hint
                prepend-inner-icon="mdi-package-variant-closed"
                dense outlined return-object
                @change="onAccountChange"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="show_all_items"
                label="Ver todos"
                inset dense hide-details
                class="mt-0 pt-0"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Cuenta contable -->
          <div class="text-caption text-uppercase grey--text text--darken-1 font-weight-bold mb-2 mt-2">
            <v-icon small>mdi-bank</v-icon> Cuenta
          </div>
          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                v-model="editForm.chart_of_account_id"
                :items="accounts"
                label="Cuenta contable"
                hint="Obligatorio"
                persistent-hint
                prepend-inner-icon="mdi-format-list-bulleted-type"
                dense outlined
                @change="onAccountChange"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="editForm.category_id"
                :items="filteredCategories"
                label="Categoría (opcional)"
                item-text="name"
                item-value="id"
                prepend-inner-icon="mdi-folder-outline"
                dense outlined clearable hide-details
                @change="editForm.subcategory_id = null"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="editForm.subcategory_id"
                :items="filteredSubCategories"
                label="Sub-Categoría (opcional)"
                item-text="name"
                item-value="id"
                prepend-inner-icon="mdi-folder-multiple-outline"
                dense outlined clearable hide-details
                no-data-text="Seleccione una categoría"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <!-- Ubicación y departamento -->
          <div class="text-caption text-uppercase grey--text text--darken-1 font-weight-bold mb-2 mt-4">
            <v-icon small>mdi-map-marker-outline</v-icon> Asignación
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="editForm.location_id"
                :items="itemsLocation"
                label="Location"
                hint="Obligatorio"
                persistent-hint
                item-text="text"
                item-value="value"
                prepend-inner-icon="mdi-map-marker"
                dense outlined
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="editForm.department_id"
                :items="itemsDepartment"
                label="Departamento"
                hint="Obligatorio"
                persistent-hint
                item-text="text"
                item-value="value"
                prepend-inner-icon="mdi-office-building-outline"
                dense outlined
              ></v-autocomplete>
            </v-col>
          </v-row>

          <!-- Monto y tipo -->
          <div class="text-caption text-uppercase grey--text text--darken-1 font-weight-bold mb-2 mt-4">
            <v-icon small>mdi-cash</v-icon> Monto
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                :items="[
                  { text: 'Débito', value: 1 },
                  { text: 'Crédito', value: 2 },
                ]"
                label="Tipo entrada"
                v-model="editForm.entry_type_id"
                item-text="text"
                item-value="value"
                prepend-inner-icon="mdi-swap-vertical"
                dense outlined hide-details
              />
            </v-col>
            <v-col cols="12" :md="isEditing ? 3 : 6">
              <v-text-field
                v-model.number="editForm.amount"
                :label="`Monto ${showGrouped ? '(÷ por item)' : ''}`"
                type="number"
                min="1"
                prepend-inner-icon="mdi-currency-usd"
                dense outlined hide-details
              />
            </v-col>
            <v-col cols="12" md="3" v-if="isEditing">
              <v-text-field
                :value="suggestedAmount"
                label="Sugerido"
                type="number"
                min="1"
                prepend-inner-icon="mdi-lightbulb-on-outline"
                dense outlined hide-details readonly
              />
            </v-col>
          </v-row>

          <!-- Alerta de descuadre -->
          <v-alert
            v-if="currentTotalAmount.toFixed(2) != totalOrderAmount.toFixed(2)"
            :type="(currentTotalAmount - totalOrderAmount) < 0 ? 'warning' : 'info'"
            dense
            text
            border="left"
            class="mt-4 mb-0"
          >
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2">
                Monto
                <strong>
                  {{ parseFloat(currentTotalAmount.toFixed(2)) - parseFloat(totalOrderAmount.toFixed(2)) < 0 ? 'sobrante' : 'faltante' }}
                </strong>
              </span>
              <span class="font-weight-bold">
                {{ (totalOrderAmount - currentTotalAmount) < 0 ? '-' : '+' }}
                {{ currencyFormat(Math.abs(totalOrderAmount - currentTotalAmount)) }}
              </span>
            </div>
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn text color="error" class="text-none" @click="showEditDialog = false">
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            class="text-none font-weight-bold rounded-lg"
            @click="saveAccount"
          >
            <v-icon left>mdi-check</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import accountCategoryRules from "../../mixins/accountCategoryRules";
export default {
  mixins: [accountCategoryRules],
  props: {
    order_item_entries: Array,
    totalOrderAmount: { type: Number, default: 0 },
    cant_edit: Boolean,
    search_names: Boolean,
    show_credits: Boolean,
    invoice_type: Number,
    cuadre_in_init: {
      type: Boolean,
      default: false,
    },
    business_unit_id: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      search:null,
      opts:[30,60,100,-1],
      show_all_items:false,
      edited_entries: [],
      itemAmountCache: {},
      rowKeyCounter: 0,
      showGrouped: true,
      showEditDialog: false,
      isEditing: false,
      editTargetKey: null,
      editForm: {
        chart_of_account_id: null,
        chart_of_account_code: "",
        chart_of_account_name: "",
        category_id: null,
        subcategory_id: null,
        location_id: null,
        department_id: 0,
        entry_type_id: 1,
        amount: 1,
        item: null
      },
      categories: [],
      accounts: [],
      itemsDepartment: [],
      itemsLocation: [],
      line_to_edit: null,
      edited:false
    };
  },
  watch: {
  order_item_entries(newValue) {//
    // Helper: identificador único de un entry (usa key si existe, fallback a item_id+entry_id)
    const entryKey = (e) => e.key || `${e.item_id}_${e.entry_id}`;

    // 1️⃣ Actualizar montos proporcionalmente solo cuando el precio/cantidad de un artículo realmente cambia
    for (const entry of newValue) {
      if (entry.amount == null) continue;
      const cacheKey = entryKey(entry);
      const itemLines = this.edited_entries.filter(e => e._entryKey === cacheKey);
      if (itemLines.length === 0) {
        // Artículo nuevo, se cachea para futuro. Se agrega en paso 2.
        this.itemAmountCache[cacheKey] = entry.amount;
        continue;
      }

      const lastAmount = this.itemAmountCache[cacheKey];
      // Solo intentar sincronizar si realmente cambió el monto del artículo
      if (lastAmount != null && Math.abs(entry.amount - lastAmount) > 0.001) {
        // Tipos 1 y 4 = factura de compra: sentido positivo → débito (1)
        // Otros tipos  = factura de venta:  sentido positivo → crédito (2)
        const isPurchase = [1, 4].includes(this.invoice_type);
        const positiveType = isPurchase ? 1 : 2;
        const negativeType = isPurchase ? 2 : 1;

        // Suma firmada actual de TODAS las líneas del artículo (no solo la cacheada).
        // Esto hace el recálculo robusto frente a ediciones manuales intermedias:
        // siempre re-sincroniza al monto real del artículo en lugar de aplicar diffs
        // ciegos que pueden desviar el total.
        const currentSignedSum = itemLines.reduce((acc, line) => {
          return acc + (line.entry_type_id === positiveType ? line.amount : -line.amount);
        }, 0);

        const drift = parseFloat((entry.amount - currentSignedSum).toFixed(2));
        if (Math.abs(drift) > 0.001) {
          // Absorber la diferencia en la primera línea
          const signedCurrent = itemLines[0].entry_type_id === positiveType
            ? itemLines[0].amount
            : -itemLines[0].amount;

          const newSigned = parseFloat((signedCurrent + drift).toFixed(2));
          if (newSigned < 0) {
            itemLines[0].amount = Math.abs(newSigned);
            itemLines[0].entry_type_id = negativeType;
          } else {
            itemLines[0].amount = newSigned;
            itemLines[0].entry_type_id = positiveType;
          }
        }
      }
      this.itemAmountCache[cacheKey] = entry.amount;
    }

    // 2️⃣ Agregar nuevos que no estén en edited_entries
    newValue.forEach(entry => {
      const ek = entryKey(entry);
      const exists = this.edited_entries.some(e => e._entryKey === ek);
      if (!exists) {
        for(let item of entry.order_chart_of_accounts){
          let selected = { code: '0', name: 'NO DEFINIDO' };
          if(this.search_names){
             selected = this.find_name(this.accounts, item.chart_of_account_id);
             if(selected==undefined){
              return
             }
          }
        this.edited_entries.push({
          _entryKey: ek,
          item_id: entry.item_id,
          entry_id: entry.entry_id,
          item_name: entry.item_name,
          chart_of_account_id: item.chart_of_account_id,
          chart_of_account_code: item.chart_of_account_code|| selected.code,
          chart_of_account_name: `${item.chart_of_account_code|| selected.code} - ${item.chart_of_account_name|| selected.name}`,
          category_id: item.category_id,
          subcategory_id: item.subcategory_id,
          location_id: item.location_id || null,
          department_id: item.department_id || 0,
          department_name: this.getDepartmentName(item.department_id),
          category_name:this.getCategoryName(item.category_id),
          subcategory_name:this.getCategoryName(item.subcategory_id),
          entry_type_id: item.entry_type_id,
          amount: item.amount,
          _rowKey: this.rowKeyCounter++,
        });
        }
        // Si el artículo fue agregado con monto negativo, asignar el tipo correcto según invoice_type
        if (entry.amount != null && entry.amount < 0) {
          const newLines = this.edited_entries.filter(e => e._entryKey === ek);
          if (newLines.length > 0) {
            // Compra negativa → crédito | Venta negativa → débito
            newLines[0].entry_type_id = [1, 4].includes(this.invoice_type) ? 2 : 1;
          }
        }
      }
    });

    // 3️⃣ Eliminar los que ya no estén en newValue
    this.edited_entries = this.edited_entries.filter(e =>
      newValue.some(nv => entryKey(nv) === e._entryKey)
    );
    if(this.show_cuadre){
      this.adjustDecimals();
    }
  }
},
  computed: {
    balanceStatus() {
      const current = Math.abs(this.currentTotalAmount);
      const target = this.totalOrderAmount || 0;
      const diff = parseFloat((current - target).toFixed(2));
      const progress = target > 0 ? Math.min((current / target) * 100, 100) : 0;
      if (Math.abs(diff) < 0.01) {
        return { color: "success", icon: "mdi-check-circle", label: "Cuadrado", progress: 100, isBalanced: true };
      }
      if (diff > 0) {
        return { color: "warning", icon: "mdi-alert", label: "Sobrante", progress, isBalanced: false };
      }
      return { color: "error", icon: "mdi-alert-circle", label: "Faltante", progress, isBalanced: false };
    },
    debitTotal() {
      return this.displayedItems.reduce(
        (sum, item) => (item.entry_type_id === 1 ? sum + item.amount : sum),
        0
      );
    },
    creditTotal() {
      return this.displayedItems.reduce(
        (sum, item) => (item.entry_type_id === 2 ? sum + item.amount : sum),
        0
      );
    },
    show_cuadre() {
     const dif = parseFloat(Math.abs(this.currentTotalAmount).toFixed(6))-parseFloat(this.totalOrderAmount.toFixed(6));
     if(Math.abs(dif)>0 && Math.abs(dif) <= 0.5){
      return true
     }
     return false;
    },
    suggestedAmount() {
        if (!this.isEditing) return "";
        const amountToMatch = this.totalOrderAmount - (parseFloat(Math.abs(this.currentTotalAmount).toFixed(6))-parseFloat(this.totalOrderAmount.toFixed(6)));
        return amountToMatch//.toFixed(2);
      },

    items_list(){    
      if(this.show_all_items){
        return this.order_item_entries.map(entry => ({
            ...entry,
            text: `${entry.item_id} - ${entry.item_name}`,
            value: entry.item_id
          }));
      }
      const items = this.order_item_entries.filter(entryA => {
        return !entryA.order_chart_of_accounts.some(accountA => {
          return this.edited_entries.some(entryB =>
            entryA.item_id === entryB.item_id 
          );
        });
      });
      for(let key in items){
        items[key].text = `${items[key].item_id} - ${items[key].item_name}`;
        items[key].value = items[key].item_id;
      }
      return items;
    },
    currentTotalAmount() {
      return parseFloat(
        this.edited_entries.reduce(
          (acc, line) => acc + ((line.entry_type_id == 2 && line.amount > 0) ? (line.amount * -1): line.amount),
          0
        )
      );
    },

    displayedItems() {
      if (this.showGrouped) {
        const groups = {};
        this.edited_entries.forEach((line) => {
          const key = `${line.chart_of_account_id}|${line.category_id}|${line.subcategory_id}|${line.department_id}|${line.location_id}|${line.entry_type_id}`;
          if (!groups[key]) {
            groups[key] = { ...line, amount: 0, _rowKey: key };
          }
          groups[key].amount += line.amount;
        });
        return Object.values(groups);
      } else {
        return this.edited_entries;
      }
    },
    tableHeaders() {
      const base = [
        { text: "ID Artículo", value: "item_id",align:"start" },
        { text: "Nombre Artículo", value: "item_name",align:"center"  },
        { text: "Cuenta", value: "chart_of_account_name" },
        { text: "Categoría", value: "category_name" },
        { text: "Subcategoría", value: "subcategory_name" },
        { text: "Location", value: "location_name" },
        { text: "Departamento", value: "department_name" },
        { text: "Monto", value: "amount",align:"end" },
        { text: "Debito", value: "debit",align:" d-none" },
        { text: "Credito", value: "credit",align:" d-none" },
        { text: "Acciones", value: "actions", sortable: false, align:"end" },
      ];
      if (!this.cant_edit) {
        base[10].align = " d-none";
      }
      if (this.showGrouped) {
        base[0].align = " d-none";
        base[1].align = " d-none";
      }
      if (this.show_credits) {
        base[7].align = " d-none";
        base[8].align = "end";
        base[9].align = "end";
      }
      return base;
    },
    filteredCategories() {
      return this.categoriesForAccount(this.categories, this.editForm.chart_of_account_id);
    },
    filteredSubCategories() {
      if(this.editForm.category_id==null){
        return [];
      }
      return this.subcategoriesForAccount(this.categories, this.editForm.chart_of_account_id, this.editForm.category_id);
    },
  },
  created() {
    this.loadCatalogs();
  },
  methods: {
    async loadCatalogs() {
      const categoriesRes = await this.api_Get(
        "accounting/categories_diary_book"
      );
      this.categories = await categoriesRes.json();
      const accountsRes = await this.api_Get("accounting/accountsUsable");
      let accounts = await accountsRes.json();
      for(let key in accounts){
        if(accounts[key].is_system_only){
          continue;
        }
        accounts[key].text = `${accounts[key].code} - ${accounts[key].name}`;
        accounts[key].value = accounts[key].id;
        this.accounts.push(accounts[key]);
      }
      this.itemsDepartment = await this.g_departments();
      this.itemsDepartment.unshift({value:0,text:"Todos"})
      for(let key in this.itemsDepartment){
        this.itemsDepartment[key].text = `${this.itemsDepartment[key].text} (${this.itemsDepartment[key].value})`;
      }
      this.itemsLocation = Object.assign([],this.$store.state.auth.locations.filter(element => this.business_unit_id > 0 ? element.bus_id === this.business_unit_id : true));

      this.initializeEditedEntries();
    },
    getCategoryName(id) {
      const found = this.categories.find((cat) => cat.id === id);
      return found ? found.name : id;
    },
    getDepartmentName(id) {
      if(id==0){
        return "TODOS (0)";
      }
      const found = this.itemsDepartment.find((dep) => dep.value === id);
      return found ? `${found.text}` : id;
    },
    getLocationName(id) {
      const found = this.itemsLocation.find((loc) => loc.value === id);
      return found ? `${found.text} (${found.desc_loc_short})` : id;
    },
    find_name(array, id) {
      return array.find((item) => item.id === id);
    },
    onAccountChange(id) {
      const selected = this.find_name(this.accounts, id);
      if (selected) {
        this.editForm.chart_of_account_code = selected.code;
        this.editForm.chart_of_account_name = `${selected.code} - ${selected.name}`;
      }
      this.resetInvalidAccountCategory(this.editForm, this.categories, id, 'category_id', 'subcategory_id');
    },
    initializeEditedEntries() {
      const entryKey = (e) => e.key || `${e.item_id}_${e.entry_id}`;
      this.order_item_entries.forEach((entry) => {
        const ek = entryKey(entry);
        // Cachear el monto inicial del artículo para detectar cambios reales en el watcher
        if (entry.amount != null) {
          this.itemAmountCache[ek] = entry.amount;
        }
        // Si el watcher ya agregó este artículo antes de que terminara loadCatalogs, no duplicar
        const alreadyAdded = this.edited_entries.some(e => e._entryKey === ek);
        if (alreadyAdded) return;
        entry.order_chart_of_accounts.forEach((account) => {
          let selected = { code: '0', name: 'NO DEFINIDO' };
          if(this.search_names){
             selected = this.find_name(this.accounts, account.chart_of_account_id);
          }
          this.edited_entries.push({
            _entryKey: ek,
            item_id: entry.item_id,
            entry_id: entry.entry_id,
            item_name: entry.item_name,
            chart_of_account_id: account.chart_of_account_id,
            chart_of_account_code: account.chart_of_account_code,
            chart_of_account_name: `${account.chart_of_account_code|| selected.code} - ${account.chart_of_account_name|| selected.name}`,
            category_id: account.category_id,
            subcategory_id: account.subcategory_id,
            location_id: account.location_id || null,
            department_id: account.department_id || 0,
            department_name: this.getDepartmentName(account.department_id),
            category_name:this.getCategoryName(account.category_id),
            subcategory_name:this.getCategoryName(account.subcategory_id),
            entry_type_id: account.entry_type_id,
            amount: account.amount, //entry.price * entry.quantity,
            _rowKey: this.rowKeyCounter++,
          });
        });
      });
      if(this.show_cuadre && this.cuadre_in_init){
        this.adjustDecimals();
        this.save_all();
      }
    },
    formatCurrency(value) {
      const safeValue = (value == null || isNaN(value)) ? 0 : value;
      return `${this.order_item_entries[0]?.currency_code || 'USD'}$${this.currencyFormat(safeValue)}`;
    },
    getAmountColor(entryTypeId) {
      if (entryTypeId === 1) return "green";
      if (entryTypeId === 2) return "red";
      return "inherit";
    },
    openEditDialog(item) {
      this.isEditing = true;
      this.line_to_edit = item;
      this.editTargetKey = `${item.chart_of_account_id}|${item.category_id}|${item.subcategory_id}|${item.location_id}|${item.department_id}|${item.entry_type_id}`;

      this.editForm = this.showGrouped? {
        ...item,
        amount: this.getGroupAmount(this.editTargetKey),
      }:item;
      this.on_off_show_all_items();
      this.showEditDialog = true;
    },
    on_off_show_all_items() {
      if(this.itemsLocation.length == 1){//SI EL USUARIO SOLO TIENE ACCESO A UNA LOCATION LA ASIGNA POR DEFECTO
        this.editForm.location_id = this.itemsLocation[0].value;
      }
      if(this.showGrouped){
        return
      }
      const items = this.order_item_entries.filter(entryA => {
        return !entryA.order_chart_of_accounts.some(accountA => {
          return this.edited_entries.some(entryB =>
            entryA.item_id === entryB.item_id 
          );
        });
      });
      for(let key in items){
        items[key].text = `${items[key].item_id} - ${items[key].item_name}`;
        items[key].value = items[key].item_id;
      }
      if(items.length > 0){
        this.show_all_items = false;
      }else{
        this.show_all_items = true;
      }
    },
    openAddDialog() {
      this.isEditing = false;
      this.editForm = {
        chart_of_account_id: null,
        chart_of_account_code: "",
        chart_of_account_name: "",
        category_id: null,
        subcategory_id: null,
        location_id: null,
        department_id: 0,
        entry_type_id: 1,
        amount: 0,
      };
      this.on_off_show_all_items();
      this.showEditDialog = true;
    },
    getGroupAmount(key) {
      return this.edited_entries
        .filter((line) => this.getKey(line) === key)
        .reduce((acc, line) => acc + line.amount, 0);
    },
    getKey(line) {
      return `${line.chart_of_account_id}|${line.category_id}|${line.subcategory_id}|${line.location_id}|${line.department_id}|${line.entry_type_id}`;
    },
    saveAccount() {
      if(this.editForm.amount==0){
        this.$toast.warning("El monto debe ser diferente a 0");
        return;
      }
      if (this.editForm.chart_of_account_id == null) {
        this.$toast.warning("Debe seleccionar una cuenta contable");
        return;
      }
      if (this.editForm.location_id == null) {
        this.$toast.warning("Debe seleccionar un location");
        return;
      }
      if (!this.showGrouped && !this.isEditing  && this.editForm.item == null) {
        this.$toast.warning("Debe seleccionar un artículo");
        return;
      }
      const categoryError = this.accountCategoryError(
        this.categories,
        this.editForm.chart_of_account_id,
        this.editForm.category_id,
        this.editForm.subcategory_id,
        this.editForm.chart_of_account_name
      );
      if (categoryError) {
        this.$toast.warning(categoryError);
        return;
      }
      if (this.editForm.category_id != null && this.editForm.subcategory_id == null) {
        this.$toast.warning("Debe seleccionar una subcategoría");
        return;
      }
      if (this.isEditing) {
        let originalLines = [];
        if(this.showGrouped){
            originalLines = this.edited_entries.filter((line) => this.getKey(line) === this.editTargetKey);
        }else{
          originalLines = this.edited_entries.filter((line) => line === this.line_to_edit);
        }
        
        const distributedAmount = this.editForm.amount / originalLines.length;
        originalLines.forEach((line) => {
          Object.assign(line, { 
              ...this.editForm,  
                item_id: line.item_id,
                entry_id: line.entry_id,
                item_name: line.item_name, 
                category_name: this.getCategoryName(this.editForm.category_id),
                subcategory_name:this.getCategoryName(this.editForm.subcategory_id),
                department_name:this.getDepartmentName(this.editForm.department_id),
                amount: this.line_to_edit.amount==this.editForm.amount?line.amount:distributedAmount 
              });
        });
      } else {
        if(this.showGrouped){
          const distributedAmount = this.editForm.amount / this.order_item_entries.length;
            this.order_item_entries.forEach((entry) => {
              this.edited_entries.push({
                item_id: entry.item_id,
                entry_id: entry.entry_id,
                item_name: entry.item_name,
                ...this.editForm,
                department_name:this.itemsDepartment.find((element)=>element.value==this.editForm.department_id).text,
                amount: distributedAmount,
                _rowKey: this.rowKeyCounter++,
              });
            });
        }else{
         // this.editForm.item.
          this.edited_entries.push({
              item_id: this.editForm.item.item_id,
              item_name: this.editForm.item.item_name,
              chart_of_account_id: this.editForm.chart_of_account_id,
              chart_of_account_code: this.editForm.chart_of_account_code,
              chart_of_account_name: this.editForm.chart_of_account_name,
              category_id: this.editForm.category_id,
              subcategory_id: this.editForm.subcategory_id,
              location_id: this.editForm.location_id,
              department_id: this.editForm.department_id,
              department_name: this.getDepartmentName(this.editForm.department_id),
              category_name:this.getCategoryName(this.editForm.category_id),
              subcategory_name:this.getCategoryName(this.editForm.subcategory_id),
              entry_type_id: 1,
              amount: this.editForm.amount,
              _rowKey: this.rowKeyCounter++,
          });
        }
      }
      this.showEditDialog = false;
      this.emit_edited_accounts();
    },
    removeAccount(item) {
      if(this.showGrouped){
        const key = this.getKey(item);
        this.edited_entries = this.edited_entries.filter((line) => this.getKey(line) !== key);
        return
      }
      const index = this.edited_entries.findIndex((line) => line === item);
      this.edited_entries.splice(index, 1);
    },
    save_all(){
      if(Math.abs(this.currentTotalAmount).toFixed(2)!=(this.totalOrderAmount).toFixed(2)){
        this.$toast.warning("El monto actual no coincide con el monto total de la orden");
        return;
      }
      const items = this.order_item_entries.filter(entryA => {
        return !entryA.order_chart_of_accounts.some(accountA => {
          return this.edited_entries.some(entryB =>
            entryA.item_id === entryB.item_id 
          );
        });
      });
      if(items.length>0){
        this.$toast.warning("Faltan articulos por codificar");
        return;
      }
      // Suponiendo que edited_entries ya tiene tus datos
          // let sequenceCounter = 0;
          // let itemCounters = {}; // Guarda cuántas veces aparece cada item_id

          // this.edited_entries.forEach(entry => {
          //   // Si es la primera vez que aparece este item_id
          //   if (!itemCounters[entry.item_id]) {
          //     sequenceCounter++; // Nuevo sequence
          //     itemCounters[entry.item_id] = { count: 0, sequence: sequenceCounter };
          //   }

          //   // Incrementar subsequence para este item_id
          //   itemCounters[entry.item_id].count++;

          //   // Asignar valores
          //   entry.sequence = itemCounters[entry.item_id].sequence;
          //   entry.subsequence = itemCounters[entry.item_id].count;
          // });
      this.$emit("set_accounts", this.edited_entries);
       this.edited=false;
    },
    emit_edited_accounts(){
      this.edited=true;
      this.$emit("emit_edited_accounts", this.edited_entries);
    },
    copyLine(item) {
      let article = this.order_item_entries.find(entry => entry.item_id === item.item_id);
      article.text = `${article.item_id} - ${article.item_name}`;
      article.value = article.item_id;  
      item.item=article;
      this.editForm={ ...item };
      this.isEditing = false;
      this.show_all_items = true;
      this.showEditDialog=true;
    },
    adjustDecimals() {
      const difference = parseFloat(Math.abs(this.currentTotalAmount).toFixed(2))-parseFloat(this.totalOrderAmount.toFixed(2));
      this.edited_entries[0].amount += (difference*-1); 
      this.edited=true;
    },
  },
};
</script>

<style scoped>
.pre-accounting-header {
  background: linear-gradient(135deg, #ffffff 0%, #f5f9ff 100%);
  border-color: #e3eafc !important;
}

.balance-panel {
  background: #fafbfc;
  border: 1px solid #e0e6ed;
  border-radius: 10px;
  padding: 10px 14px;
}

.pre-accounting-dialog-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: #fff;
}

.pre-accounting-table {
  font-size: 0.9rem;
}

.pre-accounting-table >>> .v-data-table__wrapper > table > thead > tr > th {
  font-weight: 700 !important;
  background-color: #f5f7fa !important;
  color: #37474f !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.4px;
}

.pre-accounting-table >>> .v-data-table__wrapper > table > tbody > tr {
  transition: background-color 0.15s ease;
}

.pre-accounting-table >>> .v-data-table__wrapper > table > tbody > tr:hover {
  background-color: #e3f2fd !important;
}

.pre-accounting-table >>> .v-data-table__wrapper > table > tbody > tr > td {
  border-bottom: 1px solid #eceff1 !important;
}
</style>
